"use client";
import { X } from "lucide-react";

import * as React from "react";
import OverlayWrapper from "@/components/ui/OverLayWrapper";
import DotPulseLdr from "@/components/ui/DotPulseLdr";
import { AnimatePresence, motion } from "framer-motion";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

const CONTENT_TYPE_LABELS: Record<string, string> = {
  ARTICLE: "Article",
  STATIC_PAGE: "Static Page",
  PRODUCT_LIST: "Product List",
};

const CONTENT_STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  review: "Review",
  published: "Published",
  archived: "Archived",
};

export type CloneContentSummary = {
  id: string;
  type?: string | null;
  status?: string | null;
  title_en?: string | null;
  title_fa?: string | null;
  slug_en?: string | null;
  slug_fa?: string | null;
};

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? "100%" : "-100%",
    opacity: 0,
  }),
  center: { x: 0, opacity: 1 },
  exit: (direction: number) => ({
    x: direction > 0 ? "-100%" : "100%",
    opacity: 0,
  }),
};

type CloneContentOverlayProps = {
  locale: "en" | "fa";
  onClose: () => void;
  onSelect: (content: CloneContentSummary, mode: "en" | "fa" | "both") => void;
  isCloning: boolean;
};

export function CloneContentOverlay({
  locale,
  onClose,
  onSelect,
  isCloning,
}: CloneContentOverlayProps) {
  const [search, setSearch] = React.useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [typeFilter, setTypeFilter] = React.useState<string | null>(null);
  const [items, setItems] = React.useState<CloneContentSummary[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedContent, setSelectedContent] =
    React.useState<CloneContentSummary | null>(null);
  const [viewDirection, setViewDirection] = React.useState<1 | -1>(1);
  const [typeOverlayOpen, setTypeOverlayOpen] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const fetchItems = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (debouncedSearch.trim()) {
          params.set("q", debouncedSearch.trim());
        }
        if (typeFilter) {
          params.set("type", typeFilter);
        }
        params.set("sort", "updatedAt");
        params.set("order", "desc");
        params.set("take", "100");
        const res = await fetch(`/api/cms/content?${params.toString()}`, {
          signal: controller.signal,
        });
        if (!res.ok) {
          throw new Error("Failed to load contents.");
        }
        const json = await res.json();
        if (cancelled) return;
        const fetched: CloneContentSummary[] = Array.isArray(json?.items)
          ? json.items
          : [];
        setItems(
          fetched.filter(
            (item): item is CloneContentSummary =>
              item && typeof item.id === "string"
          )
        );
      } catch (err) {
        if (cancelled) return;
        if ((err as Error)?.name === "AbortError") return;
        console.warn("clone overlay fetch failed", err);
        setError("Unable to load content list.");
        setItems([]);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    void fetchItems();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [debouncedSearch, typeFilter]);

  const groupedItems = React.useMemo(() => {
    const groups = new Map<string, CloneContentSummary[]>();
    items.forEach((item) => {
      const key = item.type || "OTHER";
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(item);
    });
    return Array.from(groups.entries()).sort((a, b) =>
      getTypeLabel(a[0]).localeCompare(getTypeLabel(b[0]))
    );
  }, [items]);

  const handleContentSelect = (item: CloneContentSummary) => {
    if (isCloning) return;
    setViewDirection(1);
    setSelectedContent(item);
  };

  const handleBackToList = () => {
    setViewDirection(-1);
    setSelectedContent(null);
  };

  const handleModeSelect = (mode: "en" | "fa" | "both") => {
    if (!selectedContent || isCloning) return;
    onSelect(selectedContent, mode);
    setSelectedContent(null);
  };

  const handleTypeSelect = (type: string | null) => {
    setTypeFilter(type);
    setTypeOverlayOpen(false);
  };

  const typeOptions = React.useMemo(
    () => Object.entries(CONTENT_TYPE_LABELS),
    []
  );

  return (
    <OverlayWrapper
      onClose={onClose}
      snapPoints={["93vh"]}
      className="bg-white"
      draggable={false}
    >
      <div className="relative h-full overflow-hidden">
        <AnimatePresence initial={false} mode="wait" custom={viewDirection}>
          {selectedContent ? (
            <motion.div
              key="confirm"
              className="flex h-full flex-col"
              custom={viewDirection}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.25, ease: "easeInOut" }}
            >
              <div className="sticky top-0 z-30 flex items-center justify-between bg-white px-4 py-3">
                <button
                  type="button"
                  className="text-lg text-gray-500 hover:opacity-70"
                  onClick={handleBackToList}
                >
                  Back
                </button>
                <div className="text-lg font-semibold">
                  {getDisplayTitle(selectedContent, locale) ||
                    selectedContent.id}
                </div>
                <button
                  type="button"
                  aria-label="Close"
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-grayCustom text-slateCustom transition hover:text-pinkCustom"
                  onClick={onClose}
                >
                  <X className="h-4 w-4" strokeWidth={4} />
                </button>
              </div>
              <div className="flex-1 space-y-5 overflow-y-auto px-4 pb-6 pt-4">
                <div className="grid gap-3">
                  <CloneActionButton
                    label="English"
                    description="Append only English blocks"
                    onClick={() => handleModeSelect("en")}
                    disabled={isCloning}
                  />
                  <CloneActionButton
                    label="Farsi"
                    description="Append only Farsi blocks"
                    onClick={() => handleModeSelect("fa")}
                    disabled={isCloning}
                  />
                  <CloneActionButton
                    label="Both"
                    description="Append English and Farsi blocks"
                    onClick={() => handleModeSelect("both")}
                    disabled={isCloning}
                  />
                </div>

                {isCloning && (
                  <div className="flex justify-center pt-2">
                    <DotPulseLdr size={36} color="var(--color-pinkCustom)" />
                  </div>
                )}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="list"
              className="relative flex h-full flex-col"
              custom={viewDirection}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.25, ease: "easeInOut" }}
            >
              <div className="sticky top-0 z-30 bg-white px-4 py-3">
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    aria-label="Close"
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-grayCustom text-slateCustom transition hover:text-pinkCustom"
                    onClick={onClose}
                  >
                    <X className="h-4 w-4" strokeWidth={4} />
                  </button>
                </div>
                <div className="mt-3 space-y-2">
                  <input
                    dir={locale === "fa" ? "rtl" : "ltr"}
                    className="w-full  border-b border-gray-200 px-3 py-2 text-base focus:border-pinkCustom focus:outline-none"
                    placeholder="Search by title or slug…"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                  <button
                    type="button"
                    className="w-full rounded border border-pinkCustom px-3 py-2 text-pinkCustom hover:bg-gray-50"
                    onClick={() => setTypeOverlayOpen(true)}
                  >
                    {typeFilter ? `${getTypeLabel(typeFilter)}` : "All types"}
                  </button>
                </div>
              </div>

              <div className="relative flex-1 overflow-y-auto px-4 pb-6 pt-4 space-y-4">
                {loading && (
                  <div className="flex justify-center py-6">
                    <DotPulseLdr size={36} color="var(--color-pinkCustom)" />
                  </div>
                )}
                {error && !loading && (
                  <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                    {error}
                  </div>
                )}
                {!loading && !error && groupedItems.length === 0 && (
                  <div className="text-center text-sm text-gray-500">
                    No content matches your search.
                  </div>
                )}
                {groupedItems.map(([type, entries]) => (
                  <section key={type}>
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {getTypeLabel(type)}
                    </div>
                    <div className="mt-2 bg-grayCustom rounded-md">
                      {entries.map((item) => {
                        const title = getDisplayTitle(item, locale);
                        const statusLabel = getStatusLabel(item.status);
                        return (
                          <button
                            type="button"
                            key={item.id}
                            className="w-full last:border-none border-b border-gray-200 px-3 py-3 text-left transition hover:border-pinkCustom focus-visible:outline-none"
                            onClick={() => handleContentSelect(item)}
                            disabled={isCloning}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="font-semibold truncate">
                                {title || item.id}
                              </div>
                              {statusLabel && (
                                <span className="text-xs text-gray-500">
                                  {statusLabel}
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </section>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {typeOverlayOpen && (
            <OverlayWrapper
              onClose={() => setTypeOverlayOpen(false)}
              snapPoints={["60vh"]}
            >
              <div className="flex items-center justify-between px-5 pb-4">
                <button
                  type="button"
                  aria-label="Close"
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-grayCustom text-slateCustom transition hover:text-pinkCustom"
                  onClick={() => setTypeOverlayOpen(false)}
                >
                  <X className="h-4 w-4" strokeWidth={4} />
                </button>
                <span className="text-base font-semibold text-gray-800">
                  Choose content type
                </span>
                <button
                  type="button"
                  className="text-lg text-pinkCustom hover:opacity-80"
                  onClick={() => handleTypeSelect(null)}
                >
                  Clear
                </button>
              </div>
              <div className="max-h-[60vh] overflow-y-auto py-2">
                <TypeOverlayRow
                  label="All types"
                  active={!typeFilter}
                  onClick={() => handleTypeSelect(null)}
                />
                {typeOptions.map(([key, label]) => (
                  <TypeOverlayRow
                    key={key}
                    label={label}
                    active={typeFilter === key}
                    onClick={() => handleTypeSelect(key)}
                  />
                ))}
              </div>
            </OverlayWrapper>
          )}
        </AnimatePresence>
      </div>
    </OverlayWrapper>
  );
}

function getTypeLabel(type?: string | null) {
  if (!type) return "Other";
  return CONTENT_TYPE_LABELS[type] ?? type.replace(/_/g, " ");
}

function getStatusLabel(status?: string | null) {
  if (!status) return "";
  return CONTENT_STATUS_LABELS[status] ?? status;
}

function pickFirstNonEmpty(...values: Array<string | null | undefined>) {
  for (const value of values) {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    }
  }
  return "";
}

function getDisplayTitle(
  data: CloneContentSummary,
  locale: "en" | "fa"
): string {
  return locale === "fa"
    ? pickFirstNonEmpty(
        data.title_fa,
        data.title_en,
        data.slug_fa,
        data.slug_en,
        data.id
      )
    : pickFirstNonEmpty(
        data.title_en,
        data.title_fa,
        data.slug_en,
        data.slug_fa,
        data.id
      );
}

function TypeOverlayRow({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`flex w-full items-center justify-between px-5 py-3 text-left text-base ${
        active ? "bg-pinkCustom/10 text-pinkCustom" : "text-gray-700"
      }`}
      onClick={onClick}
    >
      <span>{label}</span>
      <span
        className={`h-3.5 w-3.5 rounded-full border ${
          active ? "border-pinkCustom bg-pinkCustom" : "border-gray-300"
        }`}
      />
    </button>
  );
}

function CloneActionButton({
  label,
  description,
  onClick,
  disabled,
}: {
  label: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className={`rounded-md bg-pinkCustom px-4 py-3 text-left shadow-sm transition ${
        disabled
          ? "cursor-not-allowed opacity-60"
          : "hover:border-pinkCustom hover:shadow-md"
      }`}
      onClick={onClick}
      disabled={disabled}
    >
      <div className="text-base font-semibold text-white">{label}</div>
      <div className="text-sm text-gray-300">{description}</div>
    </button>
  );
}
